export const SUPPORTED_AUTHORING_LANGUAGES = [
  "en",
  "hi",
  "es",
  "ja",
  "de",
  "fr",
  "hr",
  "pl",
  "ro",
  "sk",
  "sv",
  "uk"
] as const;

export type AuthoringLibraryLanguage =
  (typeof SUPPORTED_AUTHORING_LANGUAGES)[number];

export const DEFAULT_AUTHORING_LANGUAGE: AuthoringLibraryLanguage = "en";
export const AUTHORING_LIBRARY_LANGUAGE_STORAGE_KEY =
  "frontend-authoring-language";
export const AUTHORING_LIBRARY_LANGUAGE_CHANGED_EVENT =
  "authoring-library-language-changed";

export const AUTHORING_LANGUAGE_LABELS: Record<
  AuthoringLibraryLanguage,
  string
> = {
  en: "English",
  hi: "Hindi",
  es: "Spanish",
  ja: "Japanese",
  de: "German",
  fr: "French",
  hr: "Croatian",
  pl: "Polish",
  ro: "Romanian",
  sk: "Slovak",
  sv: "Swedish",
  uk: "Ukrainian"
};

export function normalizeAuthoringLanguageCode(
  language?: string
): AuthoringLibraryLanguage {
  if (!language) {
    return DEFAULT_AUTHORING_LANGUAGE;
  }

  const normalized = language.toLowerCase().replace("_", "-");
  const languageCode = normalized.split("-")[0] as AuthoringLibraryLanguage;

  if (SUPPORTED_AUTHORING_LANGUAGES.includes(languageCode)) {
    return languageCode;
  }

  return DEFAULT_AUTHORING_LANGUAGE;
}

export function getStoredAuthoringLanguage(): AuthoringLibraryLanguage | null {
  if (typeof window === "undefined") {
    return null;
  }

  const storedLanguage = window.localStorage.getItem(
    AUTHORING_LIBRARY_LANGUAGE_STORAGE_KEY
  );

  if (!storedLanguage) {
    return null;
  }

  return normalizeAuthoringLanguageCode(storedLanguage);
}

export function setStoredAuthoringLanguage(language: string): void {
  if (typeof window === "undefined") {
    return;
  }

  const normalizedLanguage = normalizeAuthoringLanguageCode(language);
  window.localStorage.setItem(
    AUTHORING_LIBRARY_LANGUAGE_STORAGE_KEY,
    normalizedLanguage
  );
  window.dispatchEvent(new Event(AUTHORING_LIBRARY_LANGUAGE_CHANGED_EVENT));
}

export function clearStoredAuthoringLanguage(): void {
  if (typeof window === "undefined") {
    return;
  }

  window.localStorage.removeItem(AUTHORING_LIBRARY_LANGUAGE_STORAGE_KEY);
  window.dispatchEvent(new Event(AUTHORING_LIBRARY_LANGUAGE_CHANGED_EVENT));
}
